import React, { useState, StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { DownloadTester, TestResult, detectBrowser, SPEED_THRESHOLDS } from "./tester";

// Test definitions
const TESTS = [
  { id: "browser", name: "Browser Version", description: "Check if your browser is up-to-date (less than 1 year old)" },
  { id: "connection", name: "Connection Test", description: "Verify we can reach the video file server" },
  { id: "speed", name: "Download Speed", description: "Measure how fast your connection can download data" },
  { id: "integrity", name: "File Integrity", description: "Download the full file and verify it wasn't corrupted" },
] as const;

// UI Components
function StatusIcon({ status }: { status: string }) {
  if (status === "passed") {
    return (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
      </svg>
    );
  }
  if (status === "failed") {
    return (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
      </svg>
    );
  }
  if (status === "running") {
    return (
      <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
      </svg>
    );
  }
  return null;
}

function ProgressBar({ progress }: { progress: number }) {
  return (
    <div className="mt-2">
      <div className="w-full bg-gray-200 rounded-full h-2">
        <div className="bg-[#0693e3] h-2 rounded-full transition-all duration-200" style={{ width: `${progress}%` }} />
      </div>
      <div className="text-xs text-gray-500 mt-1">{progress}%</div>
    </div>
  );
}

function Spinner() {
  return (
    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
  );
}

function ArrowIcon() {
  return (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
    </svg>
  );
}

function CopyIcon() {
  return (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  );
}

function App() {
  const [results, setResults] = useState<Record<string, TestResult>>({});
  const [isRunning, setIsRunning] = useState(false);
  const [showCopied, setShowCopied] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState<number | null>(null);

  const testUrl = "https://content-assets.90seconds.com/90seconds.mp4?t=" + Date.now();

  const runTests = async () => {
    setIsRunning(true);
    setResults({});
    setDownloadProgress(null);

    const tester = new DownloadTester(testUrl);
    const activeTests = TESTS.filter(t => t.id !== "browser");

    // Initialize results as running
    setResults(activeTests.reduce((acc, t) => ({ ...acc, [t.id]: { ...t, status: "running" as const } }), {}));

    await tester.runAllTests(true, setDownloadProgress, (id, status, message, details) => {
      setResults(prev => ({
        ...prev,
        [id]: { ...TESTS.find(t => t.id === id)!, status, message, details },
      }));
    });

    setIsRunning(false);
  };

  const copyReport = () => {
    const info = detectBrowser();
    const report = [
      "Download Test Report",
      "===================",
      `Browser: ${info.browserName} ${info.browserVersion}`,
      `Date: ${new Date().toLocaleString()}`,
      "",
      "Results:",
      ...Object.values(results).map(r => {
        const icon = r.status === "passed" ? "✓" : r.status === "failed" ? "✗" : "○";
        return `${icon} ${r.name}: ${r.status.toUpperCase()}`;
      }),
      "",
      "Details:",
      ...Object.values(results).filter(r => r.message).map(r => `  • ${r.name}: ${r.message}`),
      ...Object.values(results).filter(r => r.details).map(r =>
        `  Technical Details: ${JSON.stringify(r.details, null, 2).replace(/\n/g, "\n    ")}`
      ),
    ].join("\n");

    navigator.clipboard.writeText(report);
    setShowCopied(true);
    setTimeout(() => setShowCopied(false), 2000);
  };

  const isComplete = Object.keys(results).length > 0 && !isRunning;
  const hasFailures = Object.values(results).some(r => r.status === "failed");
  const filteredTests = TESTS.filter(t => t.id === "browser");
  const activeTests = TESTS.filter(t => t.id !== "browser");

  return (
    <div className="min-h-screen bg-[#f5f5f5] text-[#32373c]">
      <header className="bg-gradient-to-r from-[#1a1a2e] via-[#16213e] to-[#0f3460] py-12 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <h1 className="text-3xl font-bold mb-2 text-white">Download Speed Test</h1>
          <p className="text-gray-300">Check if your browser can download videos from our servers</p>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm mb-6">
          <div className="px-6 py-4 border-b border-gray-100">
            <h2 className="text-base font-semibold text-[#32373c]">
              {isRunning ? "Testing in progress..." : isComplete ? "Tests completed" : "What we'll test:"}
            </h2>
          </div>
          <div className="p-6">
            <ul className="space-y-0">
              {activeTests.map((test, idx) => {
                const result = results[test.id];
                const status = result?.status || "pending";
                const showProgress = result?.id === "speed" && downloadProgress !== null;

                return (
                  <li key={test.id} className={`flex items-start gap-4 py-3 ${idx < activeTests.length - 1 ? 'border-b border-gray-100' : ''}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${
                      status === "passed" ? "bg-green-100 text-green-600" :
                      status === "failed" ? "bg-red-100 text-red-600" :
                      status === "running" ? "bg-blue-100 text-[#0693e3] animate-pulse" :
                      "bg-gray-100 text-gray-400"
                    }`}>
                      {status === "running" ? <StatusIcon status={status} /> : status === "pending" ? <span className="text-gray-400">{idx + 1}</span> : <StatusIcon status={status} />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-[#32373c]">{test.name}</div>
                      <div className={`text-sm mt-0.5 ${result?.message ? (status === "failed" ? "text-red-600" : "text-gray-600") : "text-gray-500"}`}>
                        {isComplete && result?.message ? result.message : test.description}
                      </div>
                      {showProgress && <ProgressBar progress={downloadProgress} />}
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>

        {!isComplete && (
          <button
            onClick={runTests}
            disabled={isRunning}
            className={`w-full py-3 px-6 rounded-lg font-semibold text-base transition-all flex items-center justify-center gap-2 ${
              isRunning
                ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                : "bg-[#0693e3] hover:bg-[#0583c8] text-white shadow-sm"
            }`}
          >
            {isRunning ? (
              <span className="flex items-center justify-center gap-2">
                <Spinner /> Running tests...
              </span>
            ) : (
              <>Run Test <ArrowIcon /></>
            )}
          </button>
        )}

        {isComplete && (
          <button
            onClick={copyReport}
            className={`w-full py-3 px-6 rounded-lg font-semibold text-base transition-all flex items-center justify-center gap-2 ${
              hasFailures
                ? "bg-[#ff6900] hover:bg-[#e55d00] text-white"
                : "bg-[#0693e3] hover:bg-[#0583c8] text-white"
            }`}
          >
            {showCopied ? (
              <span className="flex items-center justify-center gap-2">
                <CheckIcon /> Copied to clipboard!
              </span>
            ) : hasFailures ? (
              <>Copy failed test details <CopyIcon /></>
            ) : (
              <>Copy test results <CopyIcon /></>
            )}
          </button>
        )}
      </main>
    </div>
  );
}

const elem = document.getElementById("root")!;
const app = (
  <StrictMode>
    <App />
  </StrictMode>
);

if (import.meta.hot) {
  const root = (import.meta.hot.data.root ??= createRoot(elem));
  root.render(app);
} else {
  createRoot(elem).render(app);
}
